import React from 'react';
import {Space, Tag, Tooltip} from 'antd';
import {Link} from 'react-router-dom';
import {
  cardPaymentStatusesConstants,
  invoiceTypeConstants,
  expensePaymentMethodConstants,
  expenseTypesConstants,
  openBankingConnectionStatusConstants,
  transactionTypesConstants,
} from '../../constants';
import {DEFAULT_CURRENCY_CODE} from '../../constants/amount.constants';
import {
  FileInvoiceEuroIcon,
  ReceiptDeclinedIcon,
  ReceiptIcon,
  WireIcon,
  WithLessCreditCardIcon,
  WithLessOutsideIcon,
} from '../../icons';
import IconWrapper from '../../components/IconWrapper';
import NameWithLink from '../../components/NameWithLink';
import CreditCardDetails from '../../components/pages/CardsPage/CreditCardDetails';
import {amountHelpers} from './amount.helpers';
import {cardsHelpers} from './cards.helpers';
import {colorHelpers} from './color.helpers';
import {dateHelpers} from './date.helpers';
import {expensesHelpers} from './expenses.helpers';
import {objectHelpers} from './object.helpers';
import {tableHelpers} from './table.helpers';
import {textHelpers} from './text.helpers';
import routes from '../../routes/routes.json';
import {TRANSACTION_PAGE_SEARCH_KEYS} from '../../pages/TransactionsPage/transactionPage.constants';
import {invoicesHelpers} from './invoices.helpers';

const {CARD_TRANSACTION} = transactionTypesConstants;

const {AUTHORIZATION_ACCEPTED, CLEARED, DECLINED, REFUND, REVERSED, SETTLED} = cardPaymentStatusesConstants;

const {SELECTED_TRANSACTION_ID, SELECTED_TRANSACTION_DATE} = TRANSACTION_PAGE_SEARCH_KEYS;

const gMV = (value, currencyCode) => amountHelpers.getAmountWithCurrencyCode(value, {currencyCode});

const cardPaymentStatuses = {
  [AUTHORIZATION_ACCEPTED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [CLEARED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [DECLINED]: {
    key: 'cardPaymentStatuses.declined',
    color: '#FF7875'
  },
  [REFUND]: {
    key: 'cardPaymentStatuses.refund',
  },
  [REVERSED]: {
    key: 'cardPaymentStatuses.reversed',
  },
  [SETTLED]: {
    key: 'cardPaymentStatuses.settled',
  },
};

/**
 * Collection of transaction display, grouping, and action utility helpers.
 * @example
 * transactionsHelpers.isReturnedTransaction(transaction);
 */
export const transactionsHelpers = {
  /**
   * Marks the second-to-last item in each month group as `isLatest` to assist UI grouping display.
   * @param {Array<Object>} transactions - List of transaction objects, some of which may be month-grouping rows.
   * @param {string} summarizeKeyName - The property name that identifies a grouping/summary row.
   * @returns {Array<Object>} New transactions array with `isLatest: true` set where applicable.
   * @example
   * transactionsHelpers.additionalTransactionsGrouping(transactions, 'isMonthGrouping');
   */
  additionalTransactionsGrouping: (transactions, summarizeKeyName) => transactions.map((t, key) => (
    (key !== 0 && key !== transactions.length - 1 && transactions[key + 1][summarizeKeyName])
      ? {...t, isLatest: true} : t)
  ),
  /**
   * Returns CSS transition style objects for animating the transaction details panel and table width.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} [params.defaultTableWidth='100%'] - CSS width of the table when the details panel is hidden.
   * @param {number} params.duration - Transition duration in milliseconds.
   * @param {number} [params.detailsMaxWidth=472] - Maximum pixel width of the details panel.
   * @returns {{details: Object, table: Object}} Style objects keyed by transition state (default, entering, entered, exiting, exited).
   * @example
   * transactionsHelpers.getAnimationStyles({ duration: 300 });
   */
  getAnimationStyles: ({
    defaultTableWidth = '100%',
    duration,
    detailsMaxWidth = 472
  } = {}) => {
    const transactionDetailsMaxWidth = `${detailsMaxWidth + 24}px`;
    const tableCollapseWidth = `calc(100% - ${transactionDetailsMaxWidth})`;
    const transactionDetailsHiddenRightPosition = `calc(-${transactionDetailsMaxWidth} - 10px)`;

    return {
      details: {
        default: {
          top: 0,
          position: 'absolute',
          transition: `all ${duration}ms ease-in-out`,
          width: transactionDetailsMaxWidth
        },
        entering: {opacity: 50, right: 0},
        entered: {opacity: 1, right: 0},
        exiting: {opacity: 50, right: transactionDetailsHiddenRightPosition},
        exited: {opacity: 0, right: transactionDetailsHiddenRightPosition}
      },
      table: {
        default: {
          transition: `width ${duration}ms ease-in-out`
        },
        entering: {width: tableCollapseWidth},
        entered: {width: tableCollapseWidth},
        exiting: {width: defaultTableWidth},
        exited: {width: defaultTableWidth}
      }
    }
  },
  /**
   * Returns true when the transaction has a DECLINED or REVERSED payment status.
   * @param {Object} transaction - Transaction object.
   * @param {string} transaction.payment_status - Payment status constant.
   * @returns {boolean} True if the transaction is declined or reversed.
   * @example
   * transactionsHelpers.isReturnedTransaction({ payment_status: 'DECLINED' }); // true
   */
  isReturnedTransaction: (transaction) => transaction && [DECLINED, REVERSED].includes(transaction.payment_status),
  /**
   * Returns the transaction's currency code, checking `currency` before `currency_code`, defaulting to EUR.
   * @param {Object|null|undefined} transaction - Transaction object with an optional currency or currency_code.
   * @returns {string} ISO 4217 currency code.
   * @example
   * transactionsHelpers.getTransactionCurrencyCode({ currency_code: 'USD' }); // 'USD'
   * transactionsHelpers.getTransactionCurrencyCode({}); // 'EUR'
   */
  getTransactionCurrencyCode: (transaction) => transaction?.currency || transaction?.currency_code || DEFAULT_CURRENCY_CODE,
  /**
   * Returns a formatted amount element for a transaction row, handling both grouped and individual rows.
   * @param {Object} params - Parameters object.
   * @param {Object} params.transaction - Transaction or grouping-row object.
   * @param {string} params.variant - Column variant: 'income' or 'outcome'.
   * @param {string} [params.groupingKey='isMonthGrouping'] - Property name that flags a grouping row.
   * @returns {React.ReactElement|string|null} Formatted amount span, empty string, or null.
   * @example
   * transactionsHelpers.getTransactionAmount({ transaction, variant: 'outcome' });
   */
  getTransactionAmount: ({transaction, variant, groupingKey = 'isMonthGrouping'}) => {
    if (transaction && typeof transaction === 'object') {
      let className = 'amount';
      let amount = transaction?.amount || 0;
      const currencyCode = transactionsHelpers.getTransactionCurrencyCode(transaction);
      if (transaction[groupingKey]) {
        // A month row with no total available stays a plain separator. Falling through would print
        // 0.00, which reads as a real figure (H1MVP-4646).
        if (transaction.totalsUnavailable) return null;
        className = 'total-amount';
        amount = transaction[variant] || 0;
        if (amount > 0 && variant === 'outcome') className = `${className} danger-text`;
        if (variant === 'outcome' && Math.sign(amount) > 0) amount = amount * -1;
        return <span className={className}>{gMV(amount, currencyCode)}</span>;
      } else {
        const isIncoming = transaction.is_incoming || false;
        const isPositiveAmount = Boolean(parseFloat(amount));
        if ((isIncoming && variant !== 'income') || (!isIncoming && variant !== 'outcome')) return '';
        const price = gMV(amount, currencyCode);
        const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
        if (!isIncoming && !isReturnedTransaction) className = `${className} danger-text`;
        if (isReturnedTransaction) className = `${className} returned-price`;
        return amount ? isIncoming ? price : <span className={className}>{isPositiveAmount && '-'}{gMV(Math.abs(amount), currencyCode)}</span> : null;
      }
    } else {
      return null
    }
  },

  /**
   * Returns true when the transaction is Cash In (money received, never spend).
   * @param {Object} transaction - Transaction object.
   * @param {boolean} transaction.is_incoming - True for incoming money.
   * @returns {boolean} True if the transaction is incoming.
   * @example
   * transactionsHelpers.isCashInTransaction({ is_incoming: true }); // true
   */
  isCashInTransaction: (transaction) => Boolean(transaction?.is_incoming),

  /**
   * Returns true when the transaction is a payout (wire transfer) transaction.
   * @param {Object} transaction - Transaction object.
   * @param {string} transaction.transaction_type - Transaction type constant.
   * @returns {boolean} True if the transaction is a payout.
   * @example
   * transactionsHelpers.isPayoutTransaction({ transaction_type: 'Payout' }); // true
   */
  isPayoutTransaction: (transaction) =>
    transaction?.transaction_type === transactionTypesConstants.PAYOUT,

  /**
   * Returns true when the transaction has an amount of exactly zero and shows the "Authorized" status
   * tag (authorization accepted or cleared). A missing amount is not zero.
   * @param {Object} transaction - Transaction object.
   * @param {number} transaction.amount - Transaction amount.
   * @param {string} transaction.payment_status - Card payment status constant.
   * @returns {boolean} True if the transaction is a zero-amount authorized transaction.
   * @example
   * transactionsHelpers.isZeroAmountAuthorizedTransaction({ amount: 0, payment_status: 'A' }); // true
   * transactionsHelpers.isZeroAmountAuthorizedTransaction({ payment_status: 'A' }); // false
   */
  isZeroAmountAuthorizedTransaction: (transaction) => {
    if (transaction?.amount === undefined || transaction?.amount === null) return false;

    const authorizedStatusKey = cardPaymentStatuses[AUTHORIZATION_ACCEPTED].key;
    const statusKey = transactionsHelpers.getTransactionStatus(transaction.payment_status).key;

    return Number(transaction.amount) === 0 && statusKey === authorizedStatusKey;
  },

  /**
   * Returns true when the transaction has the REVERSED payment status. Declined and refunded
   * transactions are not reversed.
   * @param {Object} transaction - Transaction object.
   * @param {string} transaction.payment_status - Card payment status constant.
   * @returns {boolean} True if the transaction is reversed.
   * @example
   * transactionsHelpers.isReversedTransaction({ payment_status: 'V' }); // true
   * transactionsHelpers.isReversedTransaction({ payment_status: 'I' }); // false
   */
  isReversedTransaction: (transaction) => transaction?.payment_status === REVERSED,

  /**
   * Returns true when the transaction type supports an attached expense (CARD_TRANSACTION or PAYOUT).
   * @param {Object} transaction - Transaction object.
   * @param {string} transaction.transaction_type - Transaction type constant.
   * @returns {boolean} True if the transaction can have an expense.
   * @example
   * transactionsHelpers.isTransactionWithExpense({ transaction_type: 'CARD_TRANSACTION' }); // true
   */
  isTransactionWithExpense: (transaction) => {
    const transactionType = transaction?.transaction_type;
    return transactionType === transactionTypesConstants.CARD_TRANSACTION
      || transactionsHelpers.isPayoutTransaction(transaction);
  },

  /**
   * Returns a user avatar element for the transaction author when the transaction has an associated expense.
   * @param {Object} transaction - Transaction object with an optional `user` property.
   * @returns {React.ReactElement|null} Avatar element or null.
   * @example
   * transactionsHelpers.getTransactionAuthor(transaction);
   */
  getTransactionAuthor: (transaction) => {
    const user = transaction?.user || transaction?.expense?.reported_by;
    if (!user || !transactionsHelpers.isTransactionWithExpense(transaction)) return null;
    return tableHelpers.getUserAvatar({user});
  },

  /**
   * Returns the masked card number and name as a clickable link element when card and user IDs are available.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.transaction - Transaction object containing card_id, user_id, masked_pan, and card_name.
   * @param {number} [params.cardSymbolsLength=5] - Number of card digits to display.
   * @param {number} [params.nameSymbolsLength] - Maximum characters to display for the card name.
   * @returns {React.ReactElement|null} Div element with an optional link, or null if no transaction.
   * @example
   * transactionsHelpers.getTransactionCardNumberWithLink({ transaction });
   */
  getTransactionCardNumberWithLink: ({transaction, cardSymbolsLength = 5, nameSymbolsLength} = {}) => {
    if (!transaction) return null;

    const { card_id: cardId, user_id: userId } = transaction;
    const value = transactionsHelpers.getTransactionCardNumberWithName({
      transaction, cardSymbolsLength, nameSymbolsLength
    });
    const className = `d-inline-flex row-link-container${cardId && userId ? ' active-link' : ''}`;

    if (cardId && userId) {
      const url = cardsHelpers.getCardDetailsUrl({ cardId, userId });
      return (
        <div className={className}>
          <Link onClick={(e) => e.stopPropagation()} to={url}>
            <NameWithLink name={value} url={url} variant='text' />
          </Link>
        </div>
      );
    }

    return <div className={className}>{value}</div>;
  },

  /**
   * Returns the OB payment method (last-4 IBAN + bank name) as a clickable link to
   * the owning account's tab on the transactions page, when an accountId is available.
   * Mirrors getTransactionCardNumberWithLink for open banking expenses.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.obPaymentMethod - Expense ob_payment_method object with iban and bank_name.
   * @param {string} [params.accountId] - OB account id used to build the account tab link.
   * @param {number} [params.nameSymbolsLength] - Maximum characters to display for the bank name.
   * @returns {React.ReactElement|null} Div element with an optional link, or null when no payment method.
   * @example
   * transactionsHelpers.getObAccountNumberWithLink({ obPaymentMethod, accountId });
   */
  /**
   * Formats an OB payment method as "<last-4 IBAN> - <bank name>", optionally
   * truncating the bank name. Shared label builder for OB payment method displays.
   * @param {Object} obPaymentMethod - Object with iban and bank_name.
   * @param {number} [nameSymbolsLength] - Max characters for the bank name (no truncation when omitted).
   * @returns {string} Formatted label, or empty string when no payment method.
   * @example
   * transactionsHelpers.getObPaymentMethodLabel({ iban: 'DE...5555', bank_name: 'Fake Bank' }, 9); // '5555 - Fake Bank'
   */
  getObPaymentMethodLabel: (obPaymentMethod, nameSymbolsLength) => {
    if (!obPaymentMethod) return '';
    const number = cardsHelpers.getLast4Digits(obPaymentMethod?.iban);
    let name = obPaymentMethod?.bank_name || '';
    if (nameSymbolsLength) name = textHelpers.cutString(name, nameSymbolsLength);
    return `${number} - ${name}`;
  },

  getObAccountNumberWithLink: ({obPaymentMethod, accountId, nameSymbolsLength} = {}) => {
    if (!obPaymentMethod) return null;

    const value = transactionsHelpers.getObPaymentMethodLabel(obPaymentMethod, nameSymbolsLength);
    const className = `d-inline-flex row-link-container${accountId ? ' active-link' : ''}`;

    if (accountId) {
      const url = `${routes.transactionsList}#${accountId}`;
      return (
        <div className={className}>
          <Link onClick={(e) => e.stopPropagation()} to={url}>
            <NameWithLink name={value} url={url} variant='text' />
          </Link>
        </div>
      );
    }

    return <div className={className}>{value}</div>;
  },

  /**
   * Returns a formatted string combining the masked card number and card name.
   * @param {Object} params - Parameters object.
   * @param {Object} params.transaction - Transaction object with masked_pan and card_name.
   * @param {number} [params.cardSymbolsLength=5] - Number of card digits to show.
   * @param {number} [params.nameSymbolsLength] - Maximum characters for the card name; undefined means no truncation.
   * @returns {string} Formatted string in the form 'XXXXX - Card Name'.
   * @example
   * transactionsHelpers.getTransactionCardNumberWithName({ transaction, cardSymbolsLength: 4 });
   */
  getTransactionCardNumberWithName: ({transaction, cardSymbolsLength = 5, nameSymbolsLength}) => {
    let name = transaction?.card_name || '';
    const number = cardsHelpers.hideCardNumber(transaction?.masked_pan, cardSymbolsLength) || '';
    if (nameSymbolsLength) name = textHelpers.cutString(name, nameSymbolsLength);
    return `${number} - ${name}`;
  },

  /**
   * Returns true when the transaction is an Open Banking (Saltedge) transaction rather than a
   * banking (Treezor) one. `saltedge_account_id` is only ever present on raw OB transactions —
   * the same signal already used by `getSyntheticObPaymentMethod`.
   * @param {Object} transaction - Transaction object.
   * @returns {boolean} True if the transaction is an Open Banking transaction.
   * @example
   * transactionsHelpers.isOpenBankingTransaction({ saltedge_account_id: '123' }); // true
   */
  isOpenBankingTransaction: (transaction) => Boolean(transaction?.saltedge_account_id),
  /**
   * Returns the formatted date string for a banking (Treezor) transaction, preferring
   * authorization_issuer_time for declined transactions.
   * @param {Object} transaction - Transaction object with authorization_date, created_date, and payment_status.
   * @param {string} [format='LLL'] - Dayjs-compatible format string.
   * @returns {string} Formatted date string, or empty string if no date is available.
   * @example
   * transactionsHelpers.getBankingTransactionDate(transaction, 'DD MMM YYYY');
   */
  getBankingTransactionDate: (transaction, format = 'LLL') => {
    const unifiedSortDate = transaction?.unified_sort_date;
    if (unifiedSortDate) return dateHelpers.getDateFromISO(unifiedSortDate, format);

    const authorizationDate = transaction?.authorization_date;
    const isDeclined = transactionsHelpers.isDeclinedTransaction(transaction);

    // for declined transactions show authorization_issuer_time if exists
    if (isDeclined) {
      const authorizationIssuerTime = transaction?.authorization_issuer_time;
      if (authorizationIssuerTime) return dateHelpers.getDateFromISO(authorizationIssuerTime, format);
    }

    const createdDate = transaction?.created_date;
    const dateValue = authorizationDate || createdDate || null;
    return dateValue ? dateHelpers.getDateFromISO(dateValue, format) : '';
  },
  /**
   * Returns the formatted date string for a transaction, dispatching to the Open Banking or
   * banking (Treezor) date logic depending on the transaction shape.
   * @param {Object} transaction - Banking or Open Banking transaction object.
   * @param {string} [format='LLL'] - Dayjs-compatible format string.
   * @returns {string} Formatted date string, or empty string if no date is available.
   * @example
   * transactionsHelpers.getTransactionDate(transaction, 'DD MMM YYYY');
   */
  getTransactionDate: (transaction, format = 'LLL') => {
    if (transactionsHelpers.isOpenBankingTransaction(transaction)) {
      return transactionsHelpers.getOpenBankingTransactionDate(transaction, format);
    }
    return transactionsHelpers.getBankingTransactionDate(transaction, format);
  },

  /**
   * Returns the effective raw date value for an open banking transaction, preferring
   * `posting_datetime` over `made_on` since the posting time carries the full timestamp while
   * `made_on` is a date-only fallback.
   * @param {Object} transaction - Open banking transaction object.
   * @param {string} [transaction.posting_datetime] - ISO 8601 posting datetime.
   * @param {string} [transaction.made_on] - Fallback YYYY-MM-DD date string.
   * @returns {string|null} The posting_datetime, made_on, or null if neither is set.
   * @example
   * transactionsHelpers.getOpenBankingTransactionDateValue({ posting_datetime: '2026-08-25T00:00:00', made_on: '2026-08-25' });
   * // '2026-08-25T00:00:00'
   */
  getOpenBankingTransactionDateValue: (transaction) => transaction?.posting_datetime || transaction?.made_on || null,
  /**
   * Returns the formatted date string for an open banking transaction.
   * @param {Object} transaction - Open banking transaction object.
   * @param {string} [format='LLL'] - Dayjs-compatible format string.
   * @returns {string} Formatted date string, or empty string if no date is available.
   * @example
   * transactionsHelpers.getOpenBankingTransactionDate(transaction, 'DD MMM YYYY');
   */
  getOpenBankingTransactionDate: (transaction, format = 'LLL') => {
    const dateValue = transactionsHelpers.getOpenBankingTransactionDateValue(transaction);
    return dateValue ? dateHelpers.getDateFromISO(dateValue, format) : '';
  },
  /**
   * Returns either a month/year label for grouped rows or a short date for individual transaction rows.
   * @param {Object} transaction - Transaction object with created_date and optional grouping flag.
   * @param {string} groupedPropName - Property name that flags the row as a grouping row.
   * @returns {string} Formatted date string or empty string when no date is available.
   * @example
   * transactionsHelpers.getTransactionDateWithGrouping(transaction, 'isMonthGrouping');
   */
  getTransactionDateWithGrouping: (transaction, groupedPropName) => {
    const date = transaction?.created_date;
    const isGrouped = transaction?.[groupedPropName];
    if (!date) return '';
    return isGrouped
      ? dateHelpers.getDateWithMonth(date, 'MMM YYYY')
      : transactionsHelpers.getTransactionDate(transaction, 'DD MMM');
  },

  /**
   * Returns the transaction source string, falling back to the first word of the description if no source is set.
   * @param {Object} transaction - Transaction object with optional source and description fields.
   * @param {Object} [options={}] - Options object.
   * @param {number} [options.maxLength=20] - Maximum characters to display for the description fallback.
   * @returns {string} Source value or empty string.
   * @example
   * transactionsHelpers.getTransactionSource({ source: 'Stripe', description: 'Payment' }); // 'Stripe'
   */
  getTransactionSource: (transaction, {maxLength = 20} = {}) => {
    if (typeof transaction !== 'object' || !transaction) return '';

    const source = objectHelpers.getObjProp(transaction, 'source');
    const description = objectHelpers.getObjProp(transaction, 'description');
    return textHelpers.cutString(source || description || '', maxLength);
  },
  /**
   * Returns an object containing the vendor name and ID extracted from the transaction's expense.
   * @param {Object|null|undefined} transaction - Transaction object with an optional nested expense.
   * @returns {{name: string|undefined, vendor_id: string|undefined}|null} Vendor info or null if no transaction.
   * @example
   * transactionsHelpers.getTransactionVendor(transaction);
   */
  getTransactionVendor: (transaction) => {
    if (!transaction) return null;
    return  {
      name: transaction?.expense?.vendor_local_name  || transaction?.expense?.vendor,
      vendor_id: transaction?.expense?.vendor_local_id
    }
  },
  /**
   * Returns the transaction source string optionally decorated with its MCC-category icon and tooltip.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.categories=[]] - List of expense categories with mcc_code mappings.
   * @param {boolean} [params.enabledTooltip=true] - When true, wraps the icon in an Ant Design Tooltip.
   * @param {Object} params.transaction - Transaction object with mcc_code and source/description.
   * @returns {React.ReactElement|string|undefined} Source string, JSX element with icon, or undefined for grouped rows.
   * @example
   * transactionsHelpers.getTransactionSourceWithIcon({ categories, transaction });
   */
  getTransactionSourceWithIcon: ({
    categories = [],
    enabledTooltip = true,
    transaction,
  }) => {
    if (transaction?.isMonthGrouping || transaction?.isYearlyGrouping) return ;

    let value = transactionsHelpers.getTransactionSource(transaction);
    const mccCode = transaction?.mcc_code;
    const category = (
      categories.find(c => c.mcc_code === mccCode) ||
      categories.find(c => objectHelpers.isNaV(c.mcc_code))
    )?.category;

    if (category) {
      const categoryIcon = expensesHelpers.getExpenseCategoryIcon(category?.id);
      value = (
        <Space align='center' className='d-flex'>
          <div className='d-flex'>
            {enabledTooltip ? (
              <Tooltip
                title={category?.name || ''}
                trigger='hover'
              >
                <div className='d-flex'>{categoryIcon}</div>
              </Tooltip>
            ): categoryIcon}
          </div>
          <div>{value}</div>
        </Space>
      );
    }
    return value;
  },
  /**
   * Returns the status display object (key, color, bgColor) for a given card payment status.
   * @param {string} status - Card payment status constant (e.g. DECLINED, SETTLED).
   * @returns {{key: string, color: string|undefined, bgColor: string}} Status display object.
   * @example
   * transactionsHelpers.getTransactionStatus('DECLINED');
   */
  getTransactionStatus: (status) => {
    const statuses = Object.fromEntries(
      Object.entries(cardPaymentStatuses).map(([key, value]) => [
        key,
        { ...value, bgColor: value?.color ? colorHelpers.hex2rgba(value.color, 0.20) : '#F3F3F4' },
      ])
    );

    return objectHelpers.getStatus(status, statuses);
  },
  /**
   * Returns a span element displaying either a card details component or a wire icon based on transaction type.
   * @param {Object|any} transaction - Transaction object with transaction_type and optional masked_pan.
   * @returns {React.ReactElement|null} Span element or null if transaction is not an object.
   * @example
   * transactionsHelpers.getTransactionType(transaction);
   */
  getTransactionType: (transaction) => {
    if (transaction && typeof transaction === 'object') {
      const transactionType = transaction.transaction_type;
      let element = <WireIcon />;
      if (transactionType === CARD_TRANSACTION) {
        element = (
          <CreditCardDetails
            cardNumber={transaction.masked_pan}
            cardNumberClassName='card-number'
          />
        );
      }
      return <span className='transaction-type'>{element}</span>;
    } else {
      return null;
    }
  },
  /**
   * Returns the appropriate icon component for the transaction type or the outside-payment icon.
   * @param {Object|null|undefined} transaction - Transaction object with transaction_type.
   * @param {boolean} [isOutside=false] - When true, always returns the WithLessOutsideIcon.
   * @returns {React.ReactElement|null} Icon element or null if no transaction and not outside.
   * @example
   * transactionsHelpers.getTransactionTypeIcon(transaction);
   */
  getTransactionTypeIcon: (transaction, isOutside = false) => {
    if (isOutside) return <WithLessOutsideIcon />;
    if (!transaction) return null;
    return transaction.transaction_type === CARD_TRANSACTION ? <WithLessCreditCardIcon /> : <WireIcon />
  },
  /**
   * Returns a Space element displaying the payment method icon and card number link for a transaction.
   * @param {Object|null|undefined} transaction - Transaction object.
   * @returns {React.ReactElement|null} Space element or null if no transaction.
   * @example
   * transactionsHelpers.getTransactionPaymentMethodDetails(transaction);
   */
  getTransactionPaymentMethodDetails: (transaction) => {
    if (!transaction) return null;

    const isCardTransaction = transaction.transaction_type === CARD_TRANSACTION;
    const icon = transactionsHelpers.getTransactionTypeIcon(transaction);
    const value = isCardTransaction ? transactionsHelpers.getTransactionCardNumberWithLink({transaction}) : '';
    return (
      <Space align='center' size={4} className='transaction-type d-flex-child d-flex'>
        <IconWrapper size={24}>{icon}</IconWrapper>
        <span>{value}</span>
      </Space>
    );
  },
  /**
   * Formats BIC and IBAN from a wire-transfer details object by inserting spaces every 4 characters.
   * @param {Object} details - Wire transfer details object.
   * @param {string} [details.bic] - Bank Identifier Code.
   * @param {string} [details.iban] - International Bank Account Number.
   * @returns {{bic: string, iban: string}} Object with formatted BIC and IBAN strings.
   * @example
   * transactionsHelpers.getWireDetails({ bic: 'BNPAFRPP', iban: 'FR7630004000031234567890143' });
   */
  getWireDetails: (details) => {
    const gObjProp = (propName) => objectHelpers.getObjProp(details, propName);
    const getFormattedValue = (value) => value ? value.match(/.{1,4}/g).join(' ') : '';

    return {
      bic: getFormattedValue(gObjProp('bic')),
      iban: getFormattedValue(gObjProp('iban'))
    };
  },
  /**
   * Returns the invoice email address from company settings, preferring the alias over the base address.
   * @param {Object|null|undefined} settings - Company settings object.
   * @param {string} [settings.invoice_email_alias] - Preferred alias email address.
   * @param {string} [settings.invoice_email] - Fallback invoice email address.
   * @returns {string|null} Email string or null if no email is configured.
   * @example
   * transactionsHelpers.getInvoiceEmail({ invoice_email_alias: 'invoices@co.com' }); // 'invoices@co.com'
   */
  getInvoiceEmail: (settings) => {
    let email = null;
    const gObjProp = (propName) => objectHelpers.getObjProp(settings, propName);

    if (settings) email = gObjProp('invoice_email_alias') || gObjProp('invoice_email') || null;
    return email;
  },
  /**
   * Attaches a scroll event listener to the app page container that toggles a fixed details window flag,
   * and returns a cleanup function to remove the listener.
   * @param {Object} params - Parameters object.
   * @param {number} params.detailsWindowFixedHeight - Scroll offset in pixels at which the panel becomes fixed.
   * @param {boolean} params.open - Whether the details panel is currently open; listener is a no-op when false.
   * @param {boolean} params.isFixedDetailsWindow - Current fixed state value.
   * @param {Function} params.setIsFixedDetailsWindow - State setter for the fixed flag.
   * @returns {Function} Cleanup function that removes the scroll event listener.
   * @example
   * useEffect(() => transactionsHelpers.addFixedDetailsWindowScrollEventListener({ detailsWindowFixedHeight: 200, open, isFixedDetailsWindow, setIsFixedDetailsWindow }), [open]);
   */
  addFixedDetailsWindowScrollEventListener: ({detailsWindowFixedHeight, open, isFixedDetailsWindow, setIsFixedDetailsWindow}) => {
    const setScroll = (e) => {
      let scrolled = false;
      if (!open) return;
      if (e.target.scrollTop >= detailsWindowFixedHeight) scrolled = true;
      if (isFixedDetailsWindow !== scrolled) setIsFixedDetailsWindow(scrolled);
    };
    document.querySelector('#app-page-container')?.addEventListener('scroll', setScroll);
    return () => {
      document.querySelector('#app-page-container')?.removeEventListener('scroll', setScroll);
    };
  },
  /**
   * Inserts month-grouping summary rows into a flat transactions array, optionally overriding totals from an API totals list.
   * @param {Array<Object>} transactions - Flat list of transaction objects sorted by date.
   * @param {Array<{year: number, month: string, total_in: number, total_out: number}>} [totals=[]] - Monthly totals from the API.
   * @param {Object} [options] - Options.
   * @param {boolean} [options.includeTotals=true] - When false, the month rows are kept as plain
   *   separators and carry no figures. Use this when the summed rows would not describe the whole
   *   month — see H1MVP-4646: the totals endpoint only accepts year/month, so a filtered list has
   *   no true monthly total available and anything summed here would be a partial one.
   * @returns {Array<Object>} Transactions array interspersed with month-grouping rows.
   * @example
   * transactionsHelpers.getGroupedTransactions(transactions, totals);
   * transactionsHelpers.getGroupedTransactions(transactions, [], {includeTotals: false});
   */
  getGroupedTransactions: (transactions, totals = [], {includeTotals = true} = {}) => {
    const daysList = {};
    const objDatePropName = 'unified_sort_date';
    const datePropName = 'created_date';
    const summarizeKeyName = 'isMonthGrouping';
    let groupedTransactions = [];
    let lastAdded;

    // define transactions months
    transactions.forEach(transaction => {
      const date = (transaction?.[objDatePropName] || transaction?.[datePropName]).slice(0, 7);
      const {is_incoming: isIncoming} = transaction;
      const amount = Number(transaction.amount) || 0;
      const income = isIncoming ? amount : 0;
      const outcome = isIncoming ? 0 : amount;
      const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
      const hasDate = daysList.hasOwnProperty(date);
      const currencyCode = transactionsHelpers.getTransactionCurrencyCode(transaction);
      if (!isReturnedTransaction) {
        if (hasDate) {
          daysList[date] = {
            income: daysList[date].income + income,
            outcome: daysList[date].outcome + outcome,
            currency_code: currencyCode
          }
        } else {
          daysList[date] = {
            income,
            outcome,
            currency_code: currencyCode
          }
        }
      } else if (!hasDate) {
        daysList[date] = {
          income: 0,
          outcome: 0,
          currency_code: currencyCode
        }
      }
    });

    // add grouping rows to transactions list
    transactions.forEach(t => {
      const date = (t?.[objDatePropName] || t?.[datePropName]).slice(0, 7);
      if (lastAdded !== date && daysList.hasOwnProperty(date)) {
        const {income, outcome, ...rest} = daysList[date];
        let groupedTransaction = {
          id: date,
          [summarizeKeyName]: true,
          [datePropName]: date,
          // omitting the figures is not enough on its own: getTransactionAmount falls back to 0 for
          // a grouping row, so a month with no total would render a confident 0.00 rather than
          // nothing. The flag is what tells it to render neither.
          ...(includeTotals ? daysList[date] : {...rest, totalsUnavailable: true}),
        }
        const totalByDate = totals.find(t => `${t.year}-${t.month}` === date);
        if (includeTotals && totalByDate) {
          groupedTransaction = {
            ...groupedTransaction,
            income: totalByDate?.total_in,
            outcome: totalByDate?.total_out
          }
        }
        lastAdded = date;
        groupedTransactions.push(groupedTransaction);
      }
      groupedTransactions.push(t);
    });
    groupedTransactions = transactionsHelpers.additionalTransactionsGrouping(groupedTransactions, summarizeKeyName);
    return groupedTransactions;
  },
  /**
   * Whether a filter narrows a transactions list to something a MONTHLY total no longer describes.
   *
   * The distinction matters because "is this list filtered?" and "do the month figures still tell
   * the truth?" are different questions, and only the second one decides whether the figures stay.
   * Selecting a period is not narrowing in this sense: the list is still whole months, so the real
   * monthly total describes it exactly and hiding it would throw away a correct number. Anything
   * else — a search, a category, a direction, a single day — leaves a subset of the month on screen
   * that no monthly total covers.
   *
   * Both transactions tabs ask this, so it lives here rather than being decided twice.
   *
   * @param {Object} [query] - The active filter query.
   * @returns {boolean} True when the month figures should come off.
   * @example
   * transactionsHelpers.narrowsWithinMonth({year: 2026, month: 8}); // false — still a whole month
   * transactionsHelpers.narrowsWithinMonth({search: 'abc'});        // true
   */
  narrowsWithinMonth: (query = {}) => !objectHelpers.isEmptyQuery(
    Object.fromEntries(Object.entries(query || {}).filter(([key]) => !['year', 'month'].includes(key))),
  ),
  /**
   * Maps an Open Banking monthly-cashflow response into the totals shape getGroupedTransactions
   * expects.
   *
   * The cashflow response is grouped by currency and then by month; the grouping rows are matched
   * on a `YYYY-MM` key, which is already the shape of `MonthRow.month`. Amounts arrive as strings
   * and `cash_out` is positive, matching the Withless totals — getTransactionAmount is what turns a
   * positive outcome into a negative, red figure, so it must not be pre-negated here.
   *
   * @param {Array<Object>} [params.series] - `series` from GET /cashflow/monthly.
   * @param {string} [params.currencyCode] - Account currency; picks the matching currency series.
   * @returns {Array<{year: string, month: string, total_in: number, total_out: number}>} Totals,
   *   empty when the response holds nothing for that currency.
   * @example
   * transactionsHelpers.getOpenBankingMonthlyTotals({series: response.series, currencyCode: 'GBP'});
   */
  getOpenBankingMonthlyTotals: ({series, currencyCode} = {}) => {
    if (!Array.isArray(series) || !series.length) return [];
    // Fall back to the sole series only when there is exactly one: picking the first of several
    // would silently attribute another currency's figures to this account.
    const currencySeries = series.find((entry) => entry?.currency_code === currencyCode)
      || (series.length === 1 && !currencyCode ? series[0] : undefined);
    if (!currencySeries) return [];
    return (currencySeries.months ?? []).reduce((totals, row) => {
      const [year, month] = String(row?.month ?? '').split('-');
      if (!year || !month) return totals;
      return [...totals, {year, month, total_in: Number(row.cash_in) || 0, total_out: Number(row.cash_out) || 0}];
    }, []);
  },
  /**
   * Returns invoice type select options (invoice, receipt, and optionally no-receipt).
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {boolean} [params.enabledEmpty=false] - When true, appends a "no receipt" option.
   * @returns {Array<{icon: React.ReactElement, label: string, value: string}>} Invoice type options.
   * @example
   * transactionsHelpers.getInvoiceTypeOptions({ t, enabledEmpty: true });
   */
  getInvoiceTypeOptions: ({t, enabledEmpty = false}) => {
    let options = [
      {
        icon: <FileInvoiceEuroIcon />,
        label: t(`invoiceTypes.invoice`),
        value: invoiceTypeConstants.INVOICE
      },
      {
        icon: <ReceiptIcon />,
        label: t(`invoiceTypes.receipt`),
        value: invoiceTypeConstants.RECEIPT
      }
    ];
    if (enabledEmpty) {
      options = [
        ...options,
        {
          icon: <ReceiptDeclinedIcon />,
          label: t(`invoiceTypes.noReceipt`),
          value: invoiceTypeConstants.NO_RECEIPT
        }
      ];
    }
    return options;
  },
  /**
   * Returns expense type select options (invoice and receipt).
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @returns {Array<{label: string, value: string}>} Expense type options.
   * @example
   * transactionsHelpers.getExpenseTypeOptions({ t });
   */
  getExpenseTypeOptions: ({t}) => {
    return [
      {
        label: t(`expenseTypes.invoice`),
        value: expenseTypesConstants.INVOICE
      },
      {
        label: t(`expenseTypes.receipt`),
        value: expenseTypesConstants.RECEIPT
      },
    ];
  },
  /**
   * Returns true when the given table row is a monthly grouping header row.
   * @param {Object} row - Table row data object.
   * @param {boolean} [row.isMonthGrouping] - Flag set by getGroupedTransactions for summary rows.
   * @returns {boolean} True if this row is a monthly grouping row.
   * @example
   * transactionsHelpers.isMonthlyGroupedRow(row); // true for grouping rows
   */
  isMonthlyGroupedRow: (row) => row.isMonthGrouping,
  /**
   * Builds a fake open-banking payment method (`payment_method` + `ob_payment_method`) from
   * the OB account matching a transaction's `saltedge_account_id`, for transactions that have
   * no matching expense yet (or are incoming/cash-in, which never have one) so the payment
   * method can still be displayed before the transaction is reviewed.
   * @param {Object} transaction - Raw transaction object with saltedge_account_id and is_incoming.
   * @param {Array<Object>} [accounts] - List of open banking account objects with id, name, and extra.iban.
   * @param {Object} [expense] - The transaction's matched expense, if any.
   * @returns {{payment_method: string, ob_payment_method: {id: string, account_id: string, bank_name: string, iban: string}}|null}
   *   Fake payment method fields, or null when not applicable/no matching account.
   * @example
   * transactionsHelpers.getSyntheticObPaymentMethod(transaction, accounts, expense);
   */
  getSyntheticObPaymentMethod: (transaction, accounts = [], expense) => {
    const saltedgeAccountId = transaction?.saltedge_account_id;
    if (!saltedgeAccountId || (expense && !transaction?.is_incoming)) return null;

    const account = accounts.find(a => a.id === saltedgeAccountId);
    if (!account) return null;

    return {
      payment_method: expensePaymentMethodConstants.OPEN_BANKING,
      ob_payment_method: {
        // There's no real ob_payment_method_id for a synthetic entry, so `id` is just the
        // account id for uniqueness — `account_id` is what resolveObPaymentMethodInfo actually
        // links on, since it lives in a different id space than a real, backend-issued
        // ob_payment_method.id (see useObBankName.js).
        id: account.id,
        account_id: account.id,
        bank_name: account.name,
        iban: account.extra?.iban,
      },
    };
  },
  /**
   * Returns the expense-shaped object to pass into `expensesHelpers.getExpensePaymentMethod`
   * for rendering a transaction's payment method: the real expense when it already has a
   * payment method, otherwise the real expense merged with a synthetic OB one (see
   * `getSyntheticObPaymentMethod`), otherwise the real expense as-is (may be undefined).
   * @param {Object} transaction - Raw transaction object with saltedge_account_id and is_incoming.
   * @param {Array<Object>} [accounts] - List of open banking account objects with id, name, and extra.iban.
   * @param {Object} [expense] - The transaction's matched expense, if any.
   * @returns {Object|undefined} Expense-shaped object ready for `expensesHelpers.getExpensePaymentMethod`.
   * @example
   * transactionsHelpers.getPaymentMethodExpense(transaction, accounts, transaction.expense);
   */
  getPaymentMethodExpense: (transaction, accounts, expense) => {
    const obPaymentMethod = transactionsHelpers.getSyntheticObPaymentMethod(transaction, accounts, expense);
    return obPaymentMethod ? {...expense, ...obPaymentMethod} : expense;
  },
  /**
   * Merges employee and expense data into each transaction object by matching IDs.
   * When a transaction has no matching expense (or is an incoming/cash-in transaction,
   * which never has one) but carries a `saltedge_account_id`, a fake open-banking
   * expense is synthesized from the matching account so the payment method can still
   * be displayed before the transaction is reviewed.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.accounts] - List of open banking account objects with id, name, and extra.iban.
   * @param {Array<Object>} params.employees - List of employee objects with employee_id.
   * @param {Array<Object>} params.expenses - List of expense objects with id.
   * @param {Array<Object>} params.transactions - List of raw transaction objects with expense_id and user_id.
   * @returns {Array<Object>} Transactions enriched with `expense` and `user` properties.
   * @example
   * transactionsHelpers.getTransactionsWithExpense({ employees, expenses, transactions, accounts });
   */
  getTransactionsWithExpense: ({accounts = [], employees= [], expenses= [], transactions = []}) => {
    return transactions.map(t => {
      const {expense_id: expenseId, user_id: userId} = t;
      const user = employees.find(e => e.employee_id === userId);
      let expense = expenses.find(e => e.id === expenseId);

      const obPaymentMethod = transactionsHelpers.getSyntheticObPaymentMethod(t, accounts, expense);
      if (obPaymentMethod) t = {...t, ...obPaymentMethod};

      return {
        ...t,
        expense,
        user
      }
    });
  },
  /**
   * Computes the CSS class name string for a transaction table row, adding grouped and selected modifiers.
   * @param {Object} params - Parameters object.
   * @param {string} params.groupedPropName - Property name that marks a row as a grouping row.
   * @param {number} [params.index] - Current row index in the table.
   * @param {Object} params.record - Row data record.
   * @param {number} [params.selectedRowIndex] - Index of the currently selected row.
   * @returns {string} Space-separated CSS class name string.
   * @example
   * transactionsHelpers.getRowClassName({ groupedPropName: 'isMonthGrouping', index: 2, record, selectedRowIndex: 2 });
   */
  getRowClassName: ({
    groupedPropName,
    index,
    record,
    selectedRowIndex
  }) => {
    let className = '';
    if (record?.[groupedPropName]) className += ' grouped-row';
    if (index !== undefined && index === selectedRowIndex) className += ' selected-row';
    return className;
  },
  /**
   * Returns a new expenses array with the matching expense replaced by the updated expense data.
   * @param {Array<Object>} expenses - Current list of expense objects.
   * @param {Object} updatedExpense - Updated expense object; matched by id.
   * @returns {Array<Object>} New expenses array with the matching item replaced.
   * @example
   * transactionsHelpers.getUpdatedBatchExpensesList(expenses, { id: 5, category: 'Travel' });
   */
  getUpdatedBatchExpensesList: (expenses, updatedExpense) =>
    expenses.map(expense => expense.id === updatedExpense.id ? {...expense, ...updatedExpense} : expense),
  /**
   * Opens a confirmation modal and, upon confirmation, unlinks the expense from the transaction via API.
   * @param {Object} params - Parameters object.
   * @param {Function} params.t - i18n translation function.
   * @param {Function} [params.initialCallback] - Called immediately when the user confirms.
   * @param {Function} [params.successCallback] - Called after the unlink API call succeeds.
   * @param {Function} [params.errorCallback] - Called when the user cancels or the API call fails.
   * @param {Function} params.unlinkInvoices - API action to unlink the invoice.
   * @param {Function} params.onExpenseUpdate - Callback invoked with the cleared expense object after success.
   * @returns {void}
   * @example
   * transactionsHelpers.removeInvoiceFromExpense({ t, expense, unlinkExpenses, onExpenseUpdate });
   */
  removeInvoiceFromExpense: ({
    t,
    expenseId,
    invoiceId,
    initialCallback,
    successCallback,
    errorCallback,
    linkInvoices,
    unlinkInvoices,
    onExpenseUpdate
  }) => {
    if (!expenseId) errorCallback?.();

    const requestProps = {
      id: expenseId,
      data: {invoice_ids: [invoiceId]},
      errorCallback
    };

    initialCallback?.();

    unlinkInvoices?.({
      ...requestProps,
      successCallback: (expense) => {
        onExpenseUpdate?.(expense);
        successCallback?.();

        invoicesHelpers.showMatchRemovedAlert({
          translation: t,
          onClick: () => {
            initialCallback?.();
            linkInvoices({
              ...requestProps,
              successCallback: (expense) => {
                successCallback?.();
                onExpenseUpdate?.(expense);
              },
            })
          }
        });
      },
    });
  },
  /**
   * Returns the array of payment status constants that represent a declined/reversed/refunded transaction.
   * @returns {string[]} Array containing DECLINED, REVERSED, and REFUND status constants.
   * @example
   * transactionsHelpers.getDeclinedTransactionStatuses(); // ['DECLINED', 'REVERSED', 'REFUND']
   */
  getDeclinedTransactionStatuses: () => [cardPaymentStatusesConstants.DECLINED, cardPaymentStatusesConstants.REVERSED, cardPaymentStatusesConstants.REFUND],
  /**
   * Returns the right-hand content element for the transaction details header, displaying the payment status tag.
   * @param {Object} params - Parameters object.
   * @param {Object} params.transaction - Transaction object with payment_status and optional authorization_note.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement|null} Div containing a styled Tag (with Tooltip for declined), or null.
   * @example
   * transactionsHelpers.getTransactionDetailsRightContent({ transaction, t });
   */
  getTransactionDetailsRightContent: ({transaction, t}) => {
    const paymentStatus = transaction?.payment_status;
    if (!paymentStatus) return null;

    const status = transactionsHelpers.getTransactionStatus(paymentStatus);

    const tagStyle = {
      backgroundColor: status.bgColor || 'unset',
      border: 'none',
      borderRadius: 16,
      color: status.color,
      lineHeight: '24px',
      margin: 0
    };

    let tagName = t(status.key);
    const authorizationNote = transaction?.authorization_note;
    const isDeclined = paymentStatus === cardPaymentStatusesConstants.DECLINED;

    if (isDeclined && authorizationNote) tagName = `${tagName}: ${authorizationNote}`;

    const tagElement = (
      <Tag style={tagStyle}>
        {textHelpers.cutString(tagName, 22)}
      </Tag>
    );

    return (
      <div className='truncate' style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12, maxWidth: 200 }}>
        {isDeclined ? <Tooltip title={tagName}>{tagElement}</Tooltip> : tagElement}
      </div>
    );
  },
  /**
   * Returns true when the transaction's payment status is one of DECLINED, REVERSED, or REFUND.
   * @param {Object} transaction - Transaction object with a payment_status property.
   * @returns {boolean} True if the transaction is declined, reversed, or refunded.
   * @example
   * transactionsHelpers.isDeclinedTransaction({ payment_status: 'REVERSED' }); // true
   */
  isDeclinedTransaction: (transaction) =>
    transaction && transactionsHelpers.getDeclinedTransactionStatuses().includes(transaction.payment_status),
  /**
   * Returns API filter parameters for fetching transactions that are missing invoice information.
   * @param {string|number} [employeeId] - Optional employee ID to scope the filter to a specific user.
   * @returns {{payment_status_not_in: string[], has_invoice: boolean, amount_gte: number, user_id_in?: string|number}} Filter params object.
   * @example
   * transactionsHelpers.getMissingInfoFilterParams('emp_42');
   */
  getMissingInfoFilterParams: (employeeId) => {
    const params = {
      payment_status_not_in: transactionsHelpers.getDeclinedTransactionStatuses(),
      has_invoice: false,
      is_incoming: false,
      amount_gte: 0.01,
    }
    if (employeeId) {
      params.user_id_in = employeeId;
    }
    return params;
  },
  /**
   * Builds the deep-link URL to the transaction details page for a given transaction.
   * @param {Object|null|undefined} transaction - Transaction object with expense_id and created_date.
   * @returns {string|null} URL string with SELECTED_TRANSACTION_ID and SELECTED_TRANSACTION_DATE query params, or null.
   * @example
   * transactionsHelpers.getTransactionDetailsUrl(transaction);
   * // '/transactions?transactionId=123&transactionDate=2024-03-01'
   */
  getTransactionDetailsUrl: (transaction) => {
    if (!transaction) return null;

    const id = transaction.expense_id;
    if (!id) return null;

    const date = dateHelpers.getDateWithMonth(transaction.created_date, 'YYYY-MM-DD')
    return `${routes.transactionsList}?${SELECTED_TRANSACTION_ID}=${id}&${SELECTED_TRANSACTION_DATE}=${date}`;
  },
  /**
   * Formats a balance amount from a balanceDetails object into a localised currency string.
   * @param {Object} balanceDetails - Balance details object from the transaction Redux state.
   * @param {number} [balanceDetails.value] - Numeric balance amount.
   * @param {string} [balanceDetails.currency] - ISO 4217 currency code stored on the balance.
   * @param {string} [fallbackCurrency='EUR'] - Currency code to use when balanceDetails.currency is absent.
   * @returns {string} Formatted amount string, e.g. "1 234,56 EUR".
   * @example
   * transactionsHelpers.getBalanceDisplayValue({ value: 1000, currency: 'USD' }); // "1 000,00 USD"
   * transactionsHelpers.getBalanceDisplayValue({}, 'GBP'); // "0,00 GBP"
   */
  getBalanceDisplayValue: (balanceDetails, fallbackCurrency = DEFAULT_CURRENCY_CODE) => {
    const currency = balanceDetails?.currency || fallbackCurrency;
    return gMV(Number(balanceDetails?.value) || 0, currency);
  },
  /**
   * Maps a date type identifier to the corresponding database field name.
   * @param {string} date_type - Date type identifier; use 'creation_date' to get the creation timestamp field.
   * @returns {'created_at'|'expense_date'} The corresponding field name.
   * @example
   * transactionsHelpers.getDateFieldFromDateType('creation_date'); // 'created_at'
   * transactionsHelpers.getDateFieldFromDateType('expense_date');  // 'expense_date'
   */
  getDateFieldFromDateType: (date_type) => date_type === 'creation_date' ? 'created_at' : 'expense_date',
  /**
   * Returns the effective connection status when it requires a visible alert or status dot, or null when no indicator should be shown.
   * @param {Object|null|undefined} connection - Open banking connection object passed directly to `getConnectionStatus`.
   * @param {string} [connection.status] - Raw connection status string.
   * @param {string} [connection.consent_valid_until] - ISO date string representing when the consent expires.
   * @returns {string|null} The notice-worthy status string (`EXPIRED`, `EXPIRING_SOON`, or `REVOKED`), or `null` if no notice is needed.
   * @example
   * transactionsHelpers.getConnectionNoticeStatus({ status: 'active', consent_valid_until: '2024-01-01T00:00:00+00:00' }); // 'expired'
   * transactionsHelpers.getConnectionNoticeStatus({ status: 'active' }); // null
   */
  getConnectionNoticeStatus: (connection) => {
    const {EXPIRED, EXPIRING_SOON, REVOKED, DISCONNECTED, ERROR} = openBankingConnectionStatusConstants;
    const status = transactionsHelpers.getConnectionStatus(connection);
    const alertStatuses = [EXPIRED, EXPIRING_SOON, REVOKED, DISCONNECTED, ERROR];
    return alertStatuses.includes(status) ? status : null;
  },
  /**
   * Returns true when the connection is no longer active and cannot sync new data.
   * @param {Object|null|undefined} connection - Open banking connection object passed directly to `getConnectionStatus`.
   * @returns {boolean} `true` if the connection status is `REVOKED` or `EXPIRED`, `false` otherwise.
   * @example
   * transactionsHelpers.isConnectionDisconnected({ status: 'revoked' }); // true
   * transactionsHelpers.isConnectionDisconnected({ status: 'active', consent_valid_until: '2024-01-01T00:00:00+00:00' }); // true
   * transactionsHelpers.isConnectionDisconnected({ status: 'active' }); // false
   */
  isConnectionDisconnected: (connection) => {
    const {REVOKED, EXPIRED, DISCONNECTED} = openBankingConnectionStatusConstants;
    const status = transactionsHelpers.getConnectionStatus(connection);
    return [REVOKED, EXPIRED, DISCONNECTED].includes(status);
  },
  /**
   * Orders open banking accounts by their connection's creation date, oldest first, so account tabs
   * reflect when the bank was connected rather than name/IBAN. Accounts whose connection has no
   * resolvable `created_at` are left at their original position instead of being moved to an end.
   * @param {Array<Object>} accounts - Open banking accounts, each with an `id`.
   * @param {Object<string, Object>} connectionByAccountId - Map of account id to its connection object.
   * @returns {Array<Object>} A new array with the same accounts, reordered by connection creation date.
   * @example
   * transactionsHelpers.sortAccountsByConnectionDate(
   *   [{id: 'a'}, {id: 'b'}],
   *   {a: {created_at: '2024-02-01'}, b: {created_at: '2024-01-01'}}
   * ); // [{id: 'b'}, {id: 'a'}]
   */
  sortAccountsByConnectionDate: (accounts, connectionByAccountId) => {
    const sortableIndices = accounts
      .map((account, index) => (connectionByAccountId[account.id]?.created_at ? index : null))
      .filter((index) => index !== null);
    const sortedAccounts = sortableIndices
      .map((index) => accounts[index])
      .sort((a, b) => new Date(connectionByAccountId[a.id].created_at) - new Date(connectionByAccountId[b.id].created_at));
    const result = [...accounts];
    sortableIndices.forEach((originalIndex, i) => { result[originalIndex] = sortedAccounts[i]; });
    return result;
  },
  /**
   * Resolves the effective display status for an open banking connection, overriding the raw status when the consent expiry date indicates a more specific state.
   * @param {Object|null|undefined} connection - Open banking connection object.
   * @param {string} [connection.status] - Raw connection status string.
   * @param {string} [connection.consent_valid_until] - ISO date string representing when the consent expires.
   * @returns {string|undefined} `EXPIRED` if consent has already lapsed, `EXPIRING_SOON` if expiry is within 30 days, or the connection's raw status otherwise.
   * @example
   * transactionsHelpers.getConnectionStatus({ status: 'active', consent_valid_until: '2024-01-01T00:00:00+00:00' }); // 'expired'
   * transactionsHelpers.getConnectionStatus({ status: 'active', consent_valid_until: '2026-06-15T00:00:00+00:00' }); // 'expiring_soon'
   * transactionsHelpers.getConnectionStatus({ status: 'active' }); // 'active'
   */
  getConnectionStatus: (connection) => {
    const {status, consent_valid_until: consentValidUntil} = connection || {};
    if (consentValidUntil) {
      const now = new Date();
      const validUntil = new Date(consentValidUntil);
      if (validUntil < now) return openBankingConnectionStatusConstants.EXPIRED;
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      if (validUntil < thirtyDaysFromNow) return openBankingConnectionStatusConstants.EXPIRING_SOON;
    }
    return status;
  },
  getAccountName: (name) => textHelpers.cutString(name, 30),
  /**
   * Returns the account name suffixed with the masked IBAN when available.
   * @param {Object|null|undefined} account - Open banking account object.
   * @param {string} [account.name] - Account display name.
   * @param {Object} [account.extra] - Extra account details.
   * @param {string} [account.extra.iban] - Account IBAN.
   * @returns {string} Account name with a masked IBAN suffix, e.g. "Main account *3456".
   * @example
   * transactionsHelpers.getAccountNameWithIban({ name: 'Main account', extra: { iban: 'FR7630004000031234567890143' } });
   */
  getAccountNameWithIban: (account) => {
    const ibanSuffix = account?.extra?.iban ? ` *${cardsHelpers.hideCardNumber(account.extra.iban, 4)}` : '';
    return `${account?.name}${ibanSuffix}`;
  },
}
